import { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Settings, Cpu, Brain, History, BarChart3, 
  TrendingUp, CheckCircle, AlertTriangle, XCircle,
  RefreshCw, Download, Filter, ChevronLeft, ChevronRight,
  Shield, Users, Activity, LogIn, LogOut, Globe, Clock, User,
  HelpCircle, Plus, Pencil, Trash2, MessageCircle, FileCheck, FileText, CreditCard,
  Mail, Send, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';

export default function AdminSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('ai-settings');
  const [recalculating, setRecalculating] = useState(false);
  const [resetCaseId, setResetCaseId] = useState('');
  const [resettingCase, setResettingCase] = useState(false);
  
  // AI Settings
  const [aiSettings, setAiSettings] = useState({
    ai_enabled: true,
    auto_mode_globally_enabled: true,
    confidence_threshold_auto_approve: 0.85,
    confidence_threshold_medium: 0.70,
    max_file_size_mb: 10,
    log_all_analyses: true,
    daily_analysis_limit: 1000
  });
  
  // AI History
  const [aiHistory, setAiHistory] = useState([]);
  const [historyPagination, setHistoryPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [historyStats, setHistoryStats] = useState({});
  const [checkTypeBreakdown, setCheckTypeBreakdown] = useState([]);
  const [historyFilter, setHistoryFilter] = useState({ check_type: '', min_confidence: null });
  
  // AI Stats for dashboard
  const [aiStats, setAiStats] = useState({
    today_analyses: 0,
    daily_trend: [],
    overall: { total_analyses: 0, average_confidence: 0, auto_approve_rate: 0 }
  });
  
  // Activity Logs
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityPagination, setActivityPagination] = useState({ page: 1, limit: 30, total: 0, pages: 0 });
  const [activityStats, setActivityStats] = useState({
    today: { successful_logins: 0, failed_logins: 0, active_users: 0 },
    action_breakdown: [],
    daily_trend: [],
    most_active_users: [],
    suspicious_activity: []
  });
  const [activityFilter, setActivityFilter] = useState({ action_type: '', user_id: '' });

  // FAQ Management State
  const [faqCategories, setFaqCategories] = useState([]);
  const [faqItems, setFaqItems] = useState([]);
  const [faqEditMode, setFaqEditMode] = useState(null); // 'category' or 'item'
  const [faqEditItem, setFaqEditItem] = useState(null);
  const [showFaqForm, setShowFaqForm] = useState(false);
  const [faqFormType, setFaqFormType] = useState('category'); // 'category' or 'item'
  const [faqForm, setFaqForm] = useState({
    name: '',
    icon: 'HelpCircle',
    target_roles: [],
    order: 0,
    question: '',
    answer: '',
    category: ''
  });
  const [savingFaq, setSavingFaq] = useState(false);

  // SMS Settings State
  const [smsConfig, setSmsConfig] = useState({ configured: false, phone_number: null, provider: null });
  const [smsPreferences, setSmsPreferences] = useState({ enabled: true, notification_types: [] });
  const [smsLogs, setSmsLogs] = useState([]);
  const [smsLogsPage, setSmsLogsPage] = useState(1);
  const [smsLogsPagination, setSmsLogsPagination] = useState({ total: 0, pages: 1 });
  const [savingSmsPrefs, setSavingSmsPrefs] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [sendingTestSms, setSendingTestSms] = useState(false);

  // Marketing Materials State
  const [marketingMaterials, setMarketingMaterials] = useState([]);
  const [uploadingMaterial, setUploadingMaterial] = useState(false);
  const [materialForm, setMaterialForm] = useState({ name: '', type: 'brochure' });
  const [selectedFile, setSelectedFile] = useState(null);

  // Email Senders State
  const [emailSenders, setEmailSenders] = useState([]);
  const [editingEmailSender, setEditingEmailSender] = useState(null);
  const [emailSenderForm, setEmailSenderForm] = useState({ email: '', name: '', description: '' });
  const [savingEmailSender, setSavingEmailSender] = useState(false);
  const [testingEmail, setTestingEmail] = useState(null);
  const [testEmailAddress, setTestEmailAddress] = useState('');

  // Email Templates State
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({});
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [previewingTemplate, setPreviewingTemplate] = useState(null);
  const [templatePreview, setTemplatePreview] = useState(null);
  const [templateSettings, setTemplateSettings] = useState({});
  const [editingTemplateSettings, setEditingTemplateSettings] = useState(false);

  // Available roles for FAQ targeting
  const availableRoles = [
    { value: 'all', label: 'All Users' },
    { value: 'agency_admin', label: 'Agency Admin' },
    { value: 'hr_admin', label: 'HR Admin' },
    { value: 'operations_manager', label: 'Operations Manager' },
    { value: 'qc_operations', label: 'QC Operations' },
    { value: 'agent_internal', label: 'Internal Agent' },
    { value: 'agent_vendor', label: 'Vendor Agent' }
  ];

  // Available icons for categories
  const availableIcons = [
    { value: 'HelpCircle', label: 'Help Circle' },
    { value: 'FileCheck', label: 'File Check' },
    { value: 'FileText', label: 'File Text' },
    { value: 'CreditCard', label: 'Credit Card' },
    { value: 'Settings', label: 'Settings' },
    { value: 'MessageCircle', label: 'Message' }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [settingsRes, statsRes, activityStatsRes] = await Promise.all([
        axios.get('/admin/ai-settings'),
        axios.get('/admin/ai-analysis-stats'),
        axios.get('/admin/activity-stats')
      ]);
      setAiSettings(settingsRes.data);
      setAiStats(statsRes.data);
      setActivityStats(activityStatsRes.data);
      await fetchHistory(1);
      await fetchActivityLogs(1);
      await fetchFaqData();
      await fetchSmsData();
      await fetchMarketingMaterials();
      await fetchEmailSenders();
      await fetchEmailTemplates();
    } catch (error) {
      console.error('Failed to load settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchSmsData = async () => {
    try {
      const [configRes, prefsRes, logsRes] = await Promise.all([
        axios.get('/sms/config'),
        axios.get('/sms/preferences'),
        axios.get('/sms/logs?limit=10')
      ]);
      setSmsConfig(configRes.data);
      setSmsPreferences(prefsRes.data);
      setSmsLogs(logsRes.data.logs || []);
      setSmsLogsPagination(logsRes.data.pagination || { total: 0, pages: 1 });
    } catch (error) {
      console.error('Failed to load SMS data:', error);
    }
  };

  const handleUpdateSmsPreferences = async (newPrefs) => {
    setSavingSmsPrefs(true);
    try {
      await axios.put('/sms/preferences', newPrefs);
      toast.success('SMS preferences updated');
      await fetchSmsData();
    } catch (error) {
      toast.error('Failed to update SMS preferences');
    } finally {
      setSavingSmsPrefs(false);
    }
  };

  const handleToggleSmsType = async (typeId, enabled) => {
    const notifications = {};
    smsPreferences.notification_types.forEach(t => {
      notifications[t.id] = t.id === typeId ? enabled : t.enabled;
    });
    await handleUpdateSmsPreferences({
      enabled: smsPreferences.enabled,
      notifications
    });
  };

  const handleToggleAllSms = async (enabled) => {
    const notifications = {};
    smsPreferences.notification_types.forEach(t => {
      notifications[t.id] = t.enabled;
    });
    await handleUpdateSmsPreferences({
      enabled,
      notifications
    });
  };

  const handleSendTestSms = async () => {
    if (!testPhone) {
      toast.error('Please enter a phone number');
      return;
    }
    setSendingTestSms(true);
    try {
      const res = await axios.post(`/sms/test?phone_number=${encodeURIComponent(testPhone)}`);
      toast.success(`Test SMS sent! SID: ${res.data.sid}`);
      setTestPhone('');
      await fetchSmsData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test SMS');
    } finally {
      setSendingTestSms(false);
    }
  };

  const fetchSmsLogs = async (page) => {
    try {
      const res = await axios.get(`/sms/logs?page=${page}&limit=10`);
      setSmsLogs(res.data.logs || []);
      setSmsLogsPagination(res.data.pagination || { total: 0, pages: 1 });
      setSmsLogsPage(page);
    } catch (error) {
      console.error('Failed to fetch SMS logs:', error);
    }
  };

  const fetchMarketingMaterials = async () => {
    try {
      const res = await axios.get('/marketing-materials');
      setMarketingMaterials(res.data || []);
    } catch (error) {
      console.error('Failed to fetch marketing materials:', error);
    }
  };

  const handleUploadMaterial = async () => {
    if (!selectedFile || !materialForm.name) {
      toast.error('Please select a file and enter a name');
      return;
    }

    setUploadingMaterial(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('name', materialForm.name);
      formData.append('material_type', materialForm.type);

      await axios.post('/marketing-materials', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Material uploaded successfully');
      setSelectedFile(null);
      setMaterialForm({ name: '', type: 'brochure' });
      await fetchMarketingMaterials();
    } catch (error) {
      toast.error('Failed to upload material');
    } finally {
      setUploadingMaterial(false);
    }
  };

  const handleDeleteMaterial = async (materialId) => {
    if (!confirm('Are you sure you want to delete this material?')) return;
    
    try {
      await axios.delete(`/marketing-materials/${materialId}`);
      toast.success('Material deleted');
      await fetchMarketingMaterials();
    } catch (error) {
      toast.error('Failed to delete material');
    }
  };

  // Email Sender Functions
  const fetchEmailSenders = async () => {
    try {
      const res = await axios.get('/admin/email-senders');
      setEmailSenders(res.data || []);
    } catch (error) {
      console.error('Failed to load email senders:', error);
    }
  };

  const handleEditEmailSender = (sender) => {
    setEditingEmailSender(sender.email_type);
    setEmailSenderForm({
      email: sender.email,
      name: sender.name,
      description: sender.description || ''
    });
  };

  const handleSaveEmailSender = async (emailType) => {
    setSavingEmailSender(true);
    try {
      await axios.put(`/admin/email-senders/${emailType}`, {
        email_type: emailType,
        ...emailSenderForm
      });
      toast.success('Email sender updated');
      setEditingEmailSender(null);
      await fetchEmailSenders();
    } catch (error) {
      toast.error('Failed to update email sender');
    } finally {
      setSavingEmailSender(false);
    }
  };

  const handleResetEmailSender = async (emailType) => {
    if (!confirm('Reset this email sender to default configuration?')) return;
    
    try {
      await axios.delete(`/admin/email-senders/${emailType}`);
      toast.success('Email sender reset to default');
      await fetchEmailSenders();
    } catch (error) {
      toast.error('Failed to reset email sender');
    }
  };

  const handleTestEmailSender = async (emailType) => {
    if (!testEmailAddress) {
      toast.error('Please enter a test email address');
      return;
    }
    
    setTestingEmail(emailType);
    try {
      const res = await axios.post('/admin/email-senders/test', {
        email_type: emailType,
        test_email: testEmailAddress
      });
      if (res.data.success) {
        toast.success(`Test email sent to ${testEmailAddress}`);
      } else {
        toast.error('Failed to send test email');
      }
    } catch (error) {
      toast.error('Failed to send test email');
    } finally {
      setTestingEmail(null);
    }
  };

  // Email Template Functions
  const fetchEmailTemplates = async () => {
    try {
      const [templatesRes, settingsRes] = await Promise.all([
        axios.get('/admin/email-templates'),
        axios.get('/admin/email-template-settings')
      ]);
      setEmailTemplates(templatesRes.data || []);
      setTemplateSettings(settingsRes.data || {});
    } catch (error) {
      console.error('Failed to load email templates:', error);
    }
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template.template_id);
    setTemplateForm({
      name: template.name,
      subject: template.subject,
      body_html: template.body_html,
      from_type: template.from_type,
      cc_type: template.cc_type,
      sms_enabled: template.sms_enabled,
      sms_template: template.sms_template,
      is_active: template.is_active !== false
    });
    setTemplatePreview(null);
  };

  const handleSaveTemplate = async () => {
    if (!selectedTemplate) return;
    
    setSavingTemplate(true);
    try {
      await axios.put(`/admin/email-templates/${selectedTemplate}`, {
        template_id: selectedTemplate,
        ...templateForm
      });
      toast.success('Template saved');
      await fetchEmailTemplates();
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleResetTemplate = async (templateId) => {
    if (!confirm('Reset this template to default?')) return;
    
    try {
      await axios.delete(`/admin/email-templates/${templateId}`);
      toast.success('Template reset to default');
      setSelectedTemplate(null);
      setTemplateForm({});
      await fetchEmailTemplates();
    } catch (error) {
      toast.error('Failed to reset template');
    }
  };

  const handlePreviewTemplate = async (templateId) => {
    setPreviewingTemplate(templateId);
    try {
      const res = await axios.post(`/admin/email-templates/${templateId}/preview`);
      setTemplatePreview(res.data);
    } catch (error) {
      toast.error('Failed to preview template');
    } finally {
      setPreviewingTemplate(null);
    }
  };

  const handleSendTestTemplate = async () => {
    if (!selectedTemplate || !testEmailAddress) {
      toast.error('Please select a template and enter test email');
      return;
    }
    
    try {
      const res = await axios.post(`/admin/email-templates/${selectedTemplate}/send-test`, {
        test_email: testEmailAddress
      });
      if (res.data.success) {
        toast.success(`Test email sent to ${testEmailAddress}`);
      } else {
        toast.error('Failed to send test email');
      }
    } catch (error) {
      toast.error('Failed to send test email');
    }
  };

  const handleSaveTemplateSettings = async () => {
    try {
      await axios.put('/admin/email-template-settings', templateSettings);
      toast.success('Settings saved');
      setEditingTemplateSettings(false);
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const fetchFaqData = async () => {
    try {
      const [categoriesRes, itemsRes] = await Promise.all([
        axios.get('/admin/faq/categories'),
        axios.get('/admin/faq/items')
      ]);
      setFaqCategories(categoriesRes.data || []);
      setFaqItems(itemsRes.data || []);
    } catch (error) {
      console.error('Failed to load FAQ data:', error);
    }
  };

  const handleSeedFaqs = async () => {
    try {
      setSavingFaq(true);
      await axios.post('/admin/faq/seed-defaults');
      toast.success('Default FAQs seeded successfully');
      await fetchFaqData();
    } catch (error) {
      toast.error('Failed to seed FAQs');
    } finally {
      setSavingFaq(false);
    }
  };

  const openFaqForm = (type, item = null) => {
    setFaqFormType(type);
    setFaqEditItem(item);
    
    if (item) {
      if (type === 'category') {
        setFaqForm({
          name: item.name || '',
          icon: item.icon || 'HelpCircle',
          target_roles: item.target_roles || [],
          order: item.order || 0
        });
      } else {
        setFaqForm({
          question: item.question || '',
          answer: item.answer || '',
          category: item.category || '',
          target_roles: item.target_roles || [],
          order: item.order || 0
        });
      }
    } else {
      setFaqForm({
        name: '',
        icon: 'HelpCircle',
        target_roles: [],
        order: type === 'category' ? faqCategories.length + 1 : faqItems.length + 1,
        question: '',
        answer: '',
        category: faqCategories[0]?.id || ''
      });
    }
    setShowFaqForm(true);
  };

  const handleSaveFaq = async () => {
    setSavingFaq(true);
    try {
      if (faqFormType === 'category') {
        const payload = {
          name: faqForm.name,
          icon: faqForm.icon,
          target_roles: faqForm.target_roles,
          order: faqForm.order
        };
        
        if (faqEditItem) {
          await axios.put(`/admin/faq/categories/${faqEditItem.id}`, payload);
          toast.success('Category updated');
        } else {
          await axios.post('/admin/faq/categories', payload);
          toast.success('Category created');
        }
      } else {
        const payload = {
          question: faqForm.question,
          answer: faqForm.answer,
          category: faqForm.category,
          target_roles: faqForm.target_roles,
          order: faqForm.order
        };
        
        if (faqEditItem) {
          await axios.put(`/admin/faq/items/${faqEditItem.id}`, payload);
          toast.success('FAQ updated');
        } else {
          await axios.post('/admin/faq/items', payload);
          toast.success('FAQ created');
        }
      }
      
      setShowFaqForm(false);
      await fetchFaqData();
    } catch (error) {
      toast.error('Failed to save: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSavingFaq(false);
    }
  };

  const handleDeleteFaq = async (type, id) => {
    if (!confirm(`Are you sure you want to delete this ${type}?`)) return;
    
    try {
      if (type === 'category') {
        await axios.delete(`/admin/faq/categories/${id}`);
        toast.success('Category deleted');
      } else {
        await axios.delete(`/admin/faq/items/${id}`);
        toast.success('FAQ deleted');
      }
      await fetchFaqData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const toggleFaqRole = (role) => {
    setFaqForm(prev => {
      const roles = prev.target_roles || [];
      if (roles.includes(role)) {
        return { ...prev, target_roles: roles.filter(r => r !== role) };
      } else {
        return { ...prev, target_roles: [...roles, role] };
      }
    });
  };

  const fetchActivityLogs = async (page = 1) => {
    try {
      const params = new URLSearchParams({ page, limit: activityPagination.limit });
      if (activityFilter.action_type) params.append('action_type', activityFilter.action_type);
      if (activityFilter.user_id) params.append('user_id', activityFilter.user_id);
      
      const res = await axios.get(`/admin/activity-logs?${params}`);
      setActivityLogs(res.data.logs);
      setActivityPagination(res.data.pagination);
    } catch (error) {
      console.error('Failed to load activity logs:', error);
    }
  };

  const fetchHistory = async (page = 1) => {
    try {
      const params = new URLSearchParams({ page, limit: historyPagination.limit });
      if (historyFilter.check_type) params.append('check_type', historyFilter.check_type);
      if (historyFilter.min_confidence) params.append('min_confidence', historyFilter.min_confidence);
      
      const res = await axios.get(`/admin/ai-analysis-history?${params}`);
      setAiHistory(res.data.history);
      setHistoryPagination(res.data.pagination);
      setHistoryStats(res.data.stats);
      setCheckTypeBreakdown(res.data.check_type_breakdown);
    } catch (error) {
      console.error('Failed to load history:', error);
    }
  };

  const saveAiSettings = async () => {
    setSaving(true);
    try {
      await axios.put('/admin/ai-settings', aiSettings);
      toast.success('AI settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const getConfidenceColor = (score) => {
    if (score >= 0.85) return 'text-green-600 bg-green-50';
    if (score >= 0.70) return 'text-amber-600 bg-amber-50';
    return 'text-red-600 bg-red-50';
  };

  const getConfidenceBadge = (score) => {
    if (score >= 0.85) return <Badge className="bg-green-500">High</Badge>;
    if (score >= 0.70) return <Badge className="bg-amber-500">Medium</Badge>;
    return <Badge className="bg-red-500">Low</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Settings className="w-6 h-6 text-indigo-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900">Admin Settings</h1>
          </div>
          <p className="text-slate-600">Manage AI configuration and view analysis history</p>
        </div>

        {/* AI Status Cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <Card className={`${aiSettings.ai_enabled ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">AI Status</p>
                  <p className={`text-2xl font-bold ${aiSettings.ai_enabled ? 'text-green-700' : 'text-red-700'}`}>
                    {aiSettings.ai_enabled ? 'ENABLED' : 'DISABLED'}
                  </p>
                </div>
                <Cpu className={`w-10 h-10 ${aiSettings.ai_enabled ? 'text-green-500' : 'text-red-500'}`} />
              </div>
            </CardContent>
          </Card>

          {/* Recalculate Case Status Button */}
          <Card className="border-slate-200 bg-slate-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Case Status Sync</p>
                  <Button
                    data-testid="recalculate-case-status-btn"
                    variant="outline"
                    size="sm"
                    className="mt-1"
                    disabled={recalculating}
                    onClick={async () => {
                      setRecalculating(true);
                      try {
                        const res = await axios.post('/admin/recalculate-all-case-status');
                        const count = res.data?.fixed_count || 0;
                        toast.success(`Recalculated: ${count} case(s) updated`);
                      } catch (err) {
                        toast.error('Failed to recalculate');
                      } finally {
                        setRecalculating(false);
                      }
                    }}
                  >
                    {recalculating ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1" />}
                    {recalculating ? 'Running...' : 'Recalculate All'}
                  </Button>
                </div>
                <Shield className="w-10 h-10 text-slate-400" />
              </div>
            </CardContent>
          </Card>
          
          {/* Reset Individual Case */}
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm text-slate-600 mb-1">Reset Case</p>
                  <div className="flex items-center gap-2">
                    <Input
                      data-testid="reset-case-id-input"
                      placeholder="Enter Case ID"
                      value={resetCaseId}
                      onChange={(e) => setResetCaseId(e.target.value)}
                      className="h-8 text-sm w-40"
                    />
                    <Button
                      data-testid="reset-case-btn"
                      variant="destructive"
                      size="sm"
                      disabled={resettingCase || !resetCaseId.trim()}
                      onClick={async () => {
                        if (!confirm(`Are you sure you want to reset case "${resetCaseId.trim()}"? This will reset ALL checks to "Not Initiated" and clear all verification progress. This action cannot be undone.`)) return;
                        setResettingCase(true);
                        try {
                          const res = await axios.post(`/admin/reset-case/${resetCaseId.trim()}`);
                          toast.success(`Case ${resetCaseId.trim()} reset successfully. ${res.data?.checks_reset || 0} check(s) cleared.`);
                          setResetCaseId('');
                        } catch (err) {
                          const msg = err.response?.data?.detail || 'Failed to reset case';
                          toast.error(msg);
                        } finally {
                          setResettingCase(false);
                        }
                      }}
                    >
                      {resettingCase ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <RotateCcw className="w-3 h-3 mr-1" />}
                      {resettingCase ? 'Resetting...' : 'Reset'}
                    </Button>
                  </div>
                  <p className="text-xs text-amber-600 mt-1">Resets all checks to pending. Keeps employee data.</p>
                </div>
                <AlertTriangle className="w-10 h-10 text-amber-500 ml-2" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-indigo-200 bg-indigo-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Today's Analyses</p>
                  <p className="text-2xl font-bold text-indigo-700">{aiStats.today_analyses}</p>
                </div>
                <Brain className="w-10 h-10 text-indigo-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Avg Confidence</p>
                  <p className="text-2xl font-bold text-blue-700">{aiStats.overall.average_confidence}%</p>
                </div>
                <TrendingUp className="w-10 h-10 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-emerald-200 bg-emerald-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Auto-Approve Rate</p>
                  <p className="text-2xl font-bold text-emerald-700">{aiStats.overall.auto_approve_rate}%</p>
                </div>
                <CheckCircle className="w-10 h-10 text-emerald-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="ai-settings" className="flex items-center gap-2">
              <Cpu className="w-4 h-4" />
              AI Settings
            </TabsTrigger>
            <TabsTrigger value="ai-history" className="flex items-center gap-2">
              <History className="w-4 h-4" />
              Analysis History
            </TabsTrigger>
            <TabsTrigger value="ai-stats" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Statistics
            </TabsTrigger>
            <TabsTrigger value="activity-logs" className="flex items-center gap-2">
              <Activity className="w-4 h-4" />
              Activity Logs
            </TabsTrigger>
            <TabsTrigger value="faq-management" className="flex items-center gap-2" data-testid="faq-management-tab">
              <HelpCircle className="w-4 h-4" />
              FAQ Management
            </TabsTrigger>
            <TabsTrigger value="sms-settings" className="flex items-center gap-2" data-testid="sms-settings-tab">
              <MessageCircle className="w-4 h-4" />
              SMS Settings
            </TabsTrigger>
            <TabsTrigger value="email-senders" className="flex items-center gap-2" data-testid="email-senders-tab">
              <Mail className="w-4 h-4" />
              Email Senders
            </TabsTrigger>
            <TabsTrigger value="email-templates" className="flex items-center gap-2" data-testid="email-templates-tab">
              <FileText className="w-4 h-4" />
              Mail Templates
            </TabsTrigger>
            <TabsTrigger value="marketing-materials" className="flex items-center gap-2" data-testid="marketing-materials-tab">
              <FileText className="w-4 h-4" />
              Marketing
            </TabsTrigger>
          </TabsList>

          {/* AI Settings Tab */}
          <TabsContent value="ai-settings">
            <div className="grid grid-cols-2 gap-6">
              {/* Main AI Controls */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Cpu className="w-5 h-5 text-indigo-600" />
                    AI Document Analysis
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Master AI Toggle */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border">
                    <div>
                      <Label className="text-base font-semibold">Enable AI Analysis</Label>
                      <p className="text-sm text-slate-500">Master switch for all AI document analysis features</p>
                    </div>
                    <Switch
                      checked={aiSettings.ai_enabled}
                      onCheckedChange={(v) => setAiSettings(prev => ({ ...prev, ai_enabled: v }))}
                      data-testid="ai-master-toggle"
                    />
                  </div>

                  {/* Auto Mode Toggle */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border">
                    <div>
                      <Label className="text-base font-semibold">Auto Mode Globally Enabled</Label>
                      <p className="text-sm text-slate-500">Allow agents to use Auto Mode for verification</p>
                    </div>
                    <Switch
                      checked={aiSettings.auto_mode_globally_enabled}
                      onCheckedChange={(v) => setAiSettings(prev => ({ ...prev, auto_mode_globally_enabled: v }))}
                      disabled={!aiSettings.ai_enabled}
                      data-testid="auto-mode-toggle"
                    />
                  </div>

                  {/* Log All Analyses */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border">
                    <div>
                      <Label className="text-base font-semibold">Log All Analyses</Label>
                      <p className="text-sm text-slate-500">Keep history of all AI document analyses</p>
                    </div>
                    <Switch
                      checked={aiSettings.log_all_analyses}
                      onCheckedChange={(v) => setAiSettings(prev => ({ ...prev, log_all_analyses: v }))}
                      data-testid="log-analyses-toggle"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Thresholds & Limits */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="w-5 h-5 text-slate-600" />
                    Thresholds & Limits
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Auto-Approve Threshold */}
                  <div className="space-y-2">
                    <Label className="font-medium">Auto-Approve Threshold</Label>
                    <div className="flex items-center gap-4">
                      <Input
                        type="number"
                        min="0"
                        max="1"
                        step="0.05"
                        value={aiSettings.confidence_threshold_auto_approve}
                        onChange={(e) => setAiSettings(prev => ({ 
                          ...prev, 
                          confidence_threshold_auto_approve: parseFloat(e.target.value) 
                        }))}
                        className="w-24"
                        data-testid="auto-approve-threshold"
                      />
                      <span className="text-sm text-slate-500">
                        Documents with ≥{Math.round(aiSettings.confidence_threshold_auto_approve * 100)}% confidence can be auto-approved
                      </span>
                    </div>
                    <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-green-500 rounded-full"
                        style={{ width: `${aiSettings.confidence_threshold_auto_approve * 100}%` }}
                      />
                    </div>
                  </div>

                  {/* Medium Threshold */}
                  <div className="space-y-2">
                    <Label className="font-medium">Medium Confidence Threshold</Label>
                    <div className="flex items-center gap-4">
                      <Input
                        type="number"
                        min="0"
                        max="1"
                        step="0.05"
                        value={aiSettings.confidence_threshold_medium}
                        onChange={(e) => setAiSettings(prev => ({ 
                          ...prev, 
                          confidence_threshold_medium: parseFloat(e.target.value) 
                        }))}
                        className="w-24"
                        data-testid="medium-threshold"
                      />
                      <span className="text-sm text-slate-500">
                        Below {Math.round(aiSettings.confidence_threshold_medium * 100)}% = Low confidence (red warning)
                      </span>
                    </div>
                    <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-amber-500 rounded-full"
                        style={{ width: `${aiSettings.confidence_threshold_medium * 100}%` }}
                      />
                    </div>
                  </div>

                  {/* Max File Size */}
                  <div className="space-y-2">
                    <Label className="font-medium">Max File Size (MB)</Label>
                    <div className="flex items-center gap-4">
                      <Input
                        type="number"
                        min="1"
                        max="50"
                        value={aiSettings.max_file_size_mb}
                        onChange={(e) => setAiSettings(prev => ({ 
                          ...prev, 
                          max_file_size_mb: parseInt(e.target.value) 
                        }))}
                        className="w-24"
                        data-testid="max-file-size"
                      />
                      <span className="text-sm text-slate-500">Maximum allowed file size for AI analysis</span>
                    </div>
                  </div>

                  {/* Daily Limit */}
                  <div className="space-y-2">
                    <Label className="font-medium">Daily Analysis Limit</Label>
                    <div className="flex items-center gap-4">
                      <Input
                        type="number"
                        min="0"
                        max="10000"
                        value={aiSettings.daily_analysis_limit}
                        onChange={(e) => setAiSettings(prev => ({ 
                          ...prev, 
                          daily_analysis_limit: parseInt(e.target.value) 
                        }))}
                        className="w-24"
                        data-testid="daily-limit"
                      />
                      <span className="text-sm text-slate-500">Max analyses per day (0 = unlimited)</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* AI Model Info */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Brain className="w-5 h-5 text-purple-600" />
                  AI Model Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-4">
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <p className="text-sm text-purple-600 font-medium">Model</p>
                    <p className="text-lg font-bold text-purple-800">Gemini 2.5 Flash</p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-600 font-medium">Provider</p>
                    <p className="text-lg font-bold text-blue-800">Google AI</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg">
                    <p className="text-sm text-green-600 font-medium">Supported Formats</p>
                    <p className="text-lg font-bold text-green-800">PDF, JPG, PNG, WebP</p>
                  </div>
                  <div className="p-4 bg-amber-50 rounded-lg">
                    <p className="text-sm text-amber-600 font-medium">Key Source</p>
                    <p className="text-lg font-bold text-amber-800">Emergent LLM Key</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Save Button */}
            <div className="flex justify-end mt-6">
              <Button 
                onClick={saveAiSettings} 
                disabled={saving}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="save-ai-settings"
              >
                {saving ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save AI Settings'
                )}
              </Button>
            </div>
          </TabsContent>

          {/* AI History Tab */}
          <TabsContent value="ai-history">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <History className="w-5 h-5 text-slate-600" />
                    Analysis History
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Select
                      value={historyFilter.check_type || 'all'}
                      onValueChange={(v) => {
                        setHistoryFilter(prev => ({ ...prev, check_type: v === 'all' ? '' : v }));
                        fetchHistory(1);
                      }}
                    >
                      <SelectTrigger className="w-48">
                        <Filter className="w-4 h-4 mr-2" />
                        <SelectValue placeholder="Filter by type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Check Types</SelectItem>
                        <SelectItem value="employment_check">Employment</SelectItem>
                        <SelectItem value="education_check">Education</SelectItem>
                        <SelectItem value="address_check">Address</SelectItem>
                        <SelectItem value="criminal_court_check">Criminal/Court</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" onClick={() => fetchHistory(1)}>
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Stats Summary */}
                <div className="grid grid-cols-5 gap-4 mb-6">
                  <div className="p-3 bg-slate-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-slate-700">{historyStats.total_analyses || 0}</p>
                    <p className="text-xs text-slate-500">Total Analyses</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-700">{historyStats.high_confidence_count || 0}</p>
                    <p className="text-xs text-green-600">High Confidence</p>
                  </div>
                  <div className="p-3 bg-amber-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-amber-700">{historyStats.medium_confidence_count || 0}</p>
                    <p className="text-xs text-amber-600">Medium Confidence</p>
                  </div>
                  <div className="p-3 bg-red-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-red-700">{historyStats.low_confidence_count || 0}</p>
                    <p className="text-xs text-red-600">Low Confidence</p>
                  </div>
                  <div className="p-3 bg-emerald-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-emerald-700">{historyStats.auto_approve_count || 0}</p>
                    <p className="text-xs text-emerald-600">Auto-Approved</p>
                  </div>
                </div>

                {/* History Table */}
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Date</TableHead>
                        <TableHead>Check Type</TableHead>
                        <TableHead>Document</TableHead>
                        <TableHead>Confidence</TableHead>
                        <TableHead>Auto-Approve</TableHead>
                        <TableHead>Analyzed By</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {aiHistory.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                            No analysis history found
                          </TableCell>
                        </TableRow>
                      ) : (
                        aiHistory.map((item, idx) => (
                          <TableRow key={item.id || idx}>
                            <TableCell className="text-sm">
                              {item.analyzed_at ? new Date(item.analyzed_at).toLocaleString() : '-'}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="capitalize">
                                {item.check_type?.replace(/_/g, ' ') || '-'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm max-w-[200px] truncate">
                              {item.filename || '-'}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <span className={`px-2 py-1 rounded text-sm font-medium ${getConfidenceColor(item.confidence_score)}`}>
                                  {Math.round((item.confidence_score || 0) * 100)}%
                                </span>
                                {getConfidenceBadge(item.confidence_score)}
                              </div>
                            </TableCell>
                            <TableCell>
                              {item.auto_approve_eligible ? (
                                <CheckCircle className="w-5 h-5 text-green-500" />
                              ) : (
                                <XCircle className="w-5 h-5 text-slate-300" />
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-slate-600">
                              {item.user_name || 'Unknown'}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                {historyPagination.pages > 1 && (
                  <div className="flex items-center justify-between mt-4">
                    <p className="text-sm text-slate-500">
                      Showing {((historyPagination.page - 1) * historyPagination.limit) + 1} - {Math.min(historyPagination.page * historyPagination.limit, historyPagination.total)} of {historyPagination.total}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={historyPagination.page === 1}
                        onClick={() => fetchHistory(historyPagination.page - 1)}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="text-sm">Page {historyPagination.page} of {historyPagination.pages}</span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={historyPagination.page === historyPagination.pages}
                        onClick={() => fetchHistory(historyPagination.page + 1)}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* AI Stats Tab */}
          <TabsContent value="ai-stats">
            <div className="grid grid-cols-2 gap-6">
              {/* Check Type Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-indigo-600" />
                    Analysis by Check Type
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {checkTypeBreakdown.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">No data available</p>
                  ) : (
                    <div className="space-y-4">
                      {checkTypeBreakdown.map((item, idx) => (
                        <div key={idx} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium capitalize">
                              {item._id?.replace(/_/g, ' ') || 'Unknown'}
                            </span>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-slate-500">{item.count} analyses</span>
                              <Badge variant="outline">
                                {Math.round((item.avg_confidence || 0) * 100)}% avg
                              </Badge>
                            </div>
                          </div>
                          <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-indigo-500 rounded-full"
                              style={{ width: `${(item.count / (historyStats.total_analyses || 1)) * 100}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Daily Trend */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-emerald-600" />
                    Last 7 Days Trend
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {aiStats.daily_trend.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">No data available</p>
                  ) : (
                    <div className="space-y-3">
                      {aiStats.daily_trend.map((day, idx) => (
                        <div key={idx} className="flex items-center gap-4">
                          <span className="text-sm text-slate-600 w-24">{day._id}</span>
                          <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-emerald-500 rounded-full flex items-center justify-end pr-2"
                              style={{ width: `${Math.min((day.count / 50) * 100, 100)}%` }}
                            >
                              <span className="text-xs text-white font-medium">{day.count}</span>
                            </div>
                          </div>
                          <span className={`text-sm font-medium ${getConfidenceColor(day.avg_confidence).split(' ')[0]}`}>
                            {Math.round((day.avg_confidence || 0) * 100)}%
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Overall Performance */}
              <Card className="col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    Overall AI Performance
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-8">
                    <div className="text-center">
                      <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-indigo-100 mb-4">
                        <span className="text-3xl font-bold text-indigo-700">
                          {aiStats.overall.total_analyses}
                        </span>
                      </div>
                      <p className="text-slate-600">Total Analyses</p>
                    </div>
                    <div className="text-center">
                      <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-blue-100 mb-4">
                        <span className="text-3xl font-bold text-blue-700">
                          {aiStats.overall.average_confidence}%
                        </span>
                      </div>
                      <p className="text-slate-600">Average Confidence</p>
                    </div>
                    <div className="text-center">
                      <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-emerald-100 mb-4">
                        <span className="text-3xl font-bold text-emerald-700">
                          {aiStats.overall.auto_approve_rate}%
                        </span>
                      </div>
                      <p className="text-slate-600">Auto-Approve Rate</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Activity Logs Tab */}
          <TabsContent value="activity-logs">
            {/* Activity Stats Cards */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <Card className="border-green-200 bg-green-50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Today's Logins</p>
                      <p className="text-2xl font-bold text-green-700">{activityStats.today?.successful_logins || 0}</p>
                    </div>
                    <LogIn className="w-8 h-8 text-green-500" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-red-200 bg-red-50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Failed Logins</p>
                      <p className="text-2xl font-bold text-red-700">{activityStats.today?.failed_logins || 0}</p>
                    </div>
                    <AlertTriangle className="w-8 h-8 text-red-500" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-blue-200 bg-blue-50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Active Users Today</p>
                      <p className="text-2xl font-bold text-blue-700">{activityStats.today?.active_users || 0}</p>
                    </div>
                    <Users className="w-8 h-8 text-blue-500" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className={`${activityStats.suspicious_activity?.length > 0 ? 'border-amber-400 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Suspicious IPs</p>
                      <p className={`text-2xl font-bold ${activityStats.suspicious_activity?.length > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
                        {activityStats.suspicious_activity?.length || 0}
                      </p>
                    </div>
                    <Shield className={`w-8 h-8 ${activityStats.suspicious_activity?.length > 0 ? 'text-amber-500' : 'text-emerald-500'}`} />
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-3 gap-6 mb-6">
              {/* Most Active Users */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Users className="w-4 h-4 text-blue-600" />
                    Most Active Users (7 days)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {activityStats.most_active_users?.length > 0 ? (
                    <div className="space-y-2">
                      {activityStats.most_active_users.slice(0, 5).map((user, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                          <div>
                            <p className="text-sm font-medium">{user.user_name}</p>
                            <p className="text-xs text-slate-500">{user.user_email}</p>
                          </div>
                          <Badge variant="outline">{user.login_count} logins</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 text-center py-4">No data</p>
                  )}
                </CardContent>
              </Card>

              {/* Suspicious Activity */}
              <Card className={activityStats.suspicious_activity?.length > 0 ? 'border-amber-300' : ''}>
                <CardHeader>
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Shield className="w-4 h-4 text-amber-600" />
                    Suspicious Activity (Today)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {activityStats.suspicious_activity?.length > 0 ? (
                    <div className="space-y-2">
                      {activityStats.suspicious_activity.map((item, idx) => (
                        <div key={idx} className="p-2 bg-amber-50 rounded border border-amber-200">
                          <div className="flex items-center gap-2">
                            <Globe className="w-4 h-4 text-amber-600" />
                            <span className="text-sm font-mono">{item._id}</span>
                          </div>
                          <p className="text-xs text-amber-700 mt-1">
                            {item.attempts} failed attempts • {item.emails_tried?.length} emails tried
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <CheckCircle className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                      <p className="text-sm text-emerald-600">No suspicious activity</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Action Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Activity className="w-4 h-4 text-indigo-600" />
                    Action Types (7 days)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {activityStats.action_breakdown?.length > 0 ? (
                    <div className="space-y-2">
                      {activityStats.action_breakdown.slice(0, 5).map((action, idx) => (
                        <div key={idx} className="flex items-center justify-between">
                          <span className="text-sm capitalize">{action._id?.replace(/_/g, ' ')}</span>
                          <Badge variant="outline">{action.count}</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 text-center py-4">No data</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Activity Logs Table */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <History className="w-5 h-5 text-slate-600" />
                    Recent Activity
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Select
                      value={activityFilter.action_type || 'all'}
                      onValueChange={(v) => {
                        setActivityFilter(prev => ({ ...prev, action_type: v === 'all' ? '' : v }));
                        fetchActivityLogs(1);
                      }}
                    >
                      <SelectTrigger className="w-48">
                        <Filter className="w-4 h-4 mr-2" />
                        <SelectValue placeholder="Filter by type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Actions</SelectItem>
                        <SelectItem value="login_success">Successful Logins</SelectItem>
                        <SelectItem value="login_failed">Failed Logins</SelectItem>
                        <SelectItem value="login_blocked">Blocked Logins</SelectItem>
                        <SelectItem value="create">Create Actions</SelectItem>
                        <SelectItem value="update">Update Actions</SelectItem>
                        <SelectItem value="delete">Delete Actions</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" onClick={() => fetchActivityLogs(1)}>
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Time</TableHead>
                        <TableHead>User</TableHead>
                        <TableHead>Action</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>IP Address</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {activityLogs.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                            No activity logs found
                          </TableCell>
                        </TableRow>
                      ) : (
                        activityLogs.map((log, idx) => (
                          <TableRow key={log.id || idx}>
                            <TableCell className="text-sm">
                              <div className="flex items-center gap-1">
                                <Clock className="w-3 h-3 text-slate-400" />
                                {log.timestamp ? new Date(log.timestamp).toLocaleString() : '-'}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div>
                                <p className="text-sm font-medium">{log.user_name || 'Unknown'}</p>
                                <p className="text-xs text-slate-500">{log.user_email}</p>
                              </div>
                            </TableCell>
                            <TableCell className="text-sm max-w-[300px] truncate">
                              {log.action}
                            </TableCell>
                            <TableCell>
                              <Badge className={`
                                ${log.action_type === 'login_success' ? 'bg-green-100 text-green-700' : ''}
                                ${log.action_type === 'login_failed' ? 'bg-red-100 text-red-700' : ''}
                                ${log.action_type === 'login_blocked' ? 'bg-amber-100 text-amber-700' : ''}
                                ${log.action_type === 'create' ? 'bg-blue-100 text-blue-700' : ''}
                                ${log.action_type === 'update' ? 'bg-purple-100 text-purple-700' : ''}
                                ${log.action_type === 'delete' ? 'bg-rose-100 text-rose-700' : ''}
                              `}>
                                {log.action_type?.replace(/_/g, ' ')}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm font-mono text-slate-500">
                              <div className="flex items-center gap-1">
                                <Globe className="w-3 h-3" />
                                {log.ip_address || 'Unknown'}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                {activityPagination.pages > 1 && (
                  <div className="flex items-center justify-between mt-4">
                    <p className="text-sm text-slate-500">
                      Showing {((activityPagination.page - 1) * activityPagination.limit) + 1} - {Math.min(activityPagination.page * activityPagination.limit, activityPagination.total)} of {activityPagination.total}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={activityPagination.page === 1}
                        onClick={() => fetchActivityLogs(activityPagination.page - 1)}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="text-sm">Page {activityPagination.page} of {activityPagination.pages}</span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={activityPagination.page === activityPagination.pages}
                        onClick={() => fetchActivityLogs(activityPagination.page + 1)}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* FAQ Management Tab */}
          <TabsContent value="faq-management">
            <div className="space-y-6">
              {/* FAQ Header with Actions */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-slate-800">FAQ Management</h2>
                  <p className="text-sm text-slate-500">Manage FAQs shown in the Instant Helper widget</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={handleSeedFaqs}
                    disabled={savingFaq}
                    data-testid="seed-faqs-btn"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${savingFaq ? 'animate-spin' : ''}`} />
                    Seed Defaults
                  </Button>
                  <Button
                    onClick={() => openFaqForm('category')}
                    className="bg-indigo-600 hover:bg-indigo-700"
                    data-testid="add-category-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Category
                  </Button>
                  <Button
                    onClick={() => openFaqForm('item')}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="add-faq-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add FAQ
                  </Button>
                </div>
              </div>

              {/* Categories Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-indigo-600" />
                    FAQ Categories ({faqCategories.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead>Order</TableHead>
                          <TableHead>Icon</TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>Target Roles</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {faqCategories.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                              No categories yet. Click "Seed Defaults" or "Add Category" to get started.
                            </TableCell>
                          </TableRow>
                        ) : (
                          faqCategories.map((cat) => (
                            <TableRow key={cat.id}>
                              <TableCell>{cat.order}</TableCell>
                              <TableCell>
                                <Badge variant="outline" className="text-xs">{cat.icon}</Badge>
                              </TableCell>
                              <TableCell className="font-medium">{cat.name}</TableCell>
                              <TableCell>
                                <div className="flex flex-wrap gap-1">
                                  {cat.target_roles?.map(role => (
                                    <Badge key={role} variant="secondary" className="text-xs">
                                      {role}
                                    </Badge>
                                  ))}
                                </div>
                              </TableCell>
                              <TableCell>
                                {cat.is_active !== false ? (
                                  <Badge className="bg-green-100 text-green-700">Active</Badge>
                                ) : (
                                  <Badge className="bg-slate-100 text-slate-600">Inactive</Badge>
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openFaqForm('category', cat)}
                                    data-testid={`edit-category-${cat.id}`}
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteFaq('category', cat.id)}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    data-testid={`delete-category-${cat.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>

              {/* FAQ Items Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <HelpCircle className="w-5 h-5 text-emerald-600" />
                    FAQ Items ({faqItems.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead>Order</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead>Question</TableHead>
                          <TableHead>Target Roles</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {faqItems.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                              No FAQ items yet. Click "Seed Defaults" or "Add FAQ" to get started.
                            </TableCell>
                          </TableRow>
                        ) : (
                          faqItems.map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>{item.order}</TableCell>
                              <TableCell>
                                <Badge variant="outline" className="text-xs">
                                  {faqCategories.find(c => c.id === item.category)?.name || item.category}
                                </Badge>
                              </TableCell>
                              <TableCell className="max-w-[300px]">
                                <p className="font-medium truncate">{item.question}</p>
                                <p className="text-xs text-slate-500 truncate">{item.answer?.substring(0, 60)}...</p>
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-wrap gap-1">
                                  {item.target_roles?.slice(0, 2).map(role => (
                                    <Badge key={role} variant="secondary" className="text-xs">
                                      {role}
                                    </Badge>
                                  ))}
                                  {item.target_roles?.length > 2 && (
                                    <Badge variant="secondary" className="text-xs">
                                      +{item.target_roles.length - 2}
                                    </Badge>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                {item.is_active !== false ? (
                                  <Badge className="bg-green-100 text-green-700">Active</Badge>
                                ) : (
                                  <Badge className="bg-slate-100 text-slate-600">Inactive</Badge>
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openFaqForm('item', item)}
                                    data-testid={`edit-faq-${item.id}`}
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteFaq('item', item.id)}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    data-testid={`delete-faq-${item.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* FAQ Form Modal */}
            {showFaqForm && (
              <div className="fixed inset-0 z-50 flex items-center justify-center">
                <div 
                  className="absolute inset-0 bg-black/50"
                  onClick={() => setShowFaqForm(false)}
                />
                <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg p-6 m-4" data-testid="faq-form-modal">
                  <h3 className="text-lg font-semibold mb-4">
                    {faqEditItem ? 'Edit' : 'Add'} {faqFormType === 'category' ? 'Category' : 'FAQ'}
                  </h3>
                  
                  <div className="space-y-4">
                    {faqFormType === 'category' ? (
                      <>
                        <div>
                          <Label>Category Name</Label>
                          <Input
                            value={faqForm.name}
                            onChange={(e) => setFaqForm(prev => ({ ...prev, name: e.target.value }))}
                            placeholder="e.g., General, Documents, Payments"
                            data-testid="category-name-input"
                          />
                        </div>
                        <div>
                          <Label>Icon</Label>
                          <Select
                            value={faqForm.icon}
                            onValueChange={(v) => setFaqForm(prev => ({ ...prev, icon: v }))}
                          >
                            <SelectTrigger data-testid="category-icon-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {availableIcons.map(icon => (
                                <SelectItem key={icon.value} value={icon.value}>{icon.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </>
                    ) : (
                      <>
                        <div>
                          <Label>Category</Label>
                          <Select
                            value={faqForm.category}
                            onValueChange={(v) => setFaqForm(prev => ({ ...prev, category: v }))}
                          >
                            <SelectTrigger data-testid="faq-category-select">
                              <SelectValue placeholder="Select category" />
                            </SelectTrigger>
                            <SelectContent>
                              {faqCategories.map(cat => (
                                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Question</Label>
                          <Input
                            value={faqForm.question}
                            onChange={(e) => setFaqForm(prev => ({ ...prev, question: e.target.value }))}
                            placeholder="e.g., How do I reset my password?"
                            data-testid="faq-question-input"
                          />
                        </div>
                        <div>
                          <Label>Answer</Label>
                          <Textarea
                            value={faqForm.answer}
                            onChange={(e) => setFaqForm(prev => ({ ...prev, answer: e.target.value }))}
                            placeholder="Provide a detailed answer..."
                            rows={4}
                            data-testid="faq-answer-input"
                          />
                        </div>
                      </>
                    )}
                    
                    <div>
                      <Label>Target Roles</Label>
                      <p className="text-xs text-slate-500 mb-2">Select which user roles can see this</p>
                      <div className="flex flex-wrap gap-2">
                        {availableRoles.map(role => (
                          <label
                            key={role.value}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-full border cursor-pointer transition-colors ${
                              faqForm.target_roles?.includes(role.value)
                                ? 'bg-indigo-100 border-indigo-300 text-indigo-700'
                                : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                            }`}
                          >
                            <Checkbox
                              checked={faqForm.target_roles?.includes(role.value)}
                              onCheckedChange={() => toggleFaqRole(role.value)}
                              className="hidden"
                            />
                            <span className="text-sm">{role.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <Label>Display Order</Label>
                      <Input
                        type="number"
                        value={faqForm.order}
                        onChange={(e) => setFaqForm(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                        min={0}
                        className="w-24"
                        data-testid="faq-order-input"
                      />
                    </div>
                  </div>
                  
                  <div className="flex justify-end gap-2 mt-6">
                    <Button variant="outline" onClick={() => setShowFaqForm(false)}>
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSaveFaq}
                      disabled={savingFaq}
                      className="bg-indigo-600 hover:bg-indigo-700"
                      data-testid="save-faq-btn"
                    >
                      {savingFaq ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        'Save'
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* SMS Settings Tab */}
          <TabsContent value="sms-settings">
            <div className="space-y-6">
              {/* SMS Configuration Status */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageCircle className="w-5 h-5 text-emerald-600" />
                    SMS Configuration
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium">Provider</p>
                          <p className="text-sm text-slate-500">{smsConfig.provider || 'Not configured'}</p>
                        </div>
                        <Badge className={smsConfig.configured ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                          {smsConfig.configured ? 'Connected' : 'Not Connected'}
                        </Badge>
                      </div>
                      {smsConfig.phone_number && (
                        <div className="p-4 bg-slate-50 rounded-lg">
                          <p className="font-medium">Sender Number</p>
                          <p className="text-lg text-indigo-600 font-mono">{smsConfig.phone_number}</p>
                        </div>
                      )}
                    </div>
                    <div className="space-y-4">
                      <div className="p-4 border rounded-lg">
                        <Label className="mb-2 block">Send Test SMS</Label>
                        <div className="flex gap-2">
                          <Input
                            placeholder="+919876543210"
                            value={testPhone}
                            onChange={(e) => setTestPhone(e.target.value)}
                            className="flex-1"
                            data-testid="test-sms-phone-input"
                          />
                          <Button
                            onClick={handleSendTestSms}
                            disabled={sendingTestSms || !smsConfig.configured}
                            className="bg-emerald-600 hover:bg-emerald-700"
                            data-testid="send-test-sms-btn"
                          >
                            {sendingTestSms ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              'Send Test'
                            )}
                          </Button>
                        </div>
                        <p className="text-xs text-slate-500 mt-2">Include country code (e.g., +91 for India)</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* SMS Notification Preferences */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <Settings className="w-5 h-5 text-indigo-600" />
                      Notification Preferences
                    </CardTitle>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-slate-500">Master Switch</span>
                      <Switch
                        checked={smsPreferences.enabled}
                        onCheckedChange={(checked) => handleToggleAllSms(checked)}
                        disabled={savingSmsPrefs}
                        data-testid="sms-master-switch"
                      />
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {smsPreferences.notification_types?.map((type) => (
                      <div
                        key={type.id}
                        className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
                          type.enabled && smsPreferences.enabled
                            ? 'bg-emerald-50 border-emerald-200'
                            : 'bg-slate-50 border-slate-200'
                        }`}
                        data-testid={`sms-type-${type.id}`}
                      >
                        <div>
                          <p className="font-medium">{type.name}</p>
                          <p className="text-sm text-slate-500">{type.description}</p>
                        </div>
                        <Switch
                          checked={type.enabled}
                          onCheckedChange={(checked) => handleToggleSmsType(type.id, checked)}
                          disabled={savingSmsPrefs || !smsPreferences.enabled}
                          data-testid={`sms-toggle-${type.id}`}
                        />
                      </div>
                    ))}
                    {(!smsPreferences.notification_types || smsPreferences.notification_types.length === 0) && (
                      <p className="text-center text-slate-500 py-8">No notification types configured</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Recent SMS Logs */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <History className="w-5 h-5 text-slate-600" />
                    Recent SMS Logs
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead>Phone</TableHead>
                          <TableHead>Message</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Sent At</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {smsLogs.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                              No SMS logs yet
                            </TableCell>
                          </TableRow>
                        ) : (
                          smsLogs.map((log) => (
                            <TableRow key={log.id}>
                              <TableCell className="font-mono text-sm">{log.to_phone}</TableCell>
                              <TableCell className="max-w-[300px]">
                                <p className="truncate text-sm">{log.message}</p>
                              </TableCell>
                              <TableCell>
                                <Badge className={
                                  log.status === 'delivered' || log.status === 'sent' || log.status === 'queued'
                                    ? 'bg-green-100 text-green-700'
                                    : log.status === 'failed'
                                    ? 'bg-red-100 text-red-700'
                                    : 'bg-slate-100 text-slate-600'
                                }>
                                  {log.status}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-sm text-slate-500">
                                {new Date(log.sent_at).toLocaleString()}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                  {smsLogsPagination.pages > 1 && (
                    <div className="flex items-center justify-between mt-4">
                      <p className="text-sm text-slate-500">
                        Page {smsLogsPage} of {smsLogsPagination.pages} ({smsLogsPagination.total} total)
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => fetchSmsLogs(smsLogsPage - 1)}
                          disabled={smsLogsPage <= 1}
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => fetchSmsLogs(smsLogsPage + 1)}
                          disabled={smsLogsPage >= smsLogsPagination.pages}
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Email Senders Tab */}
          <TabsContent value="email-senders">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="w-5 h-5 text-indigo-600" />
                    Email Sender Configuration
                  </CardTitle>
                  <p className="text-sm text-slate-500 mt-1">
                    Configure team-specific sender emails for different types of notifications.
                    All emails must be verified in SendGrid before use.
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="mb-4">
                    <Label>Test Email Address</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        type="email"
                        placeholder="Enter email to receive test emails"
                        value={testEmailAddress}
                        onChange={(e) => setTestEmailAddress(e.target.value)}
                        className="max-w-xs"
                      />
                    </div>
                  </div>
                  
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email Type</TableHead>
                        <TableHead>Sender Email</TableHead>
                        <TableHead>Sender Name</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {emailSenders.map((sender) => (
                        <TableRow key={sender.email_type}>
                          <TableCell className="font-medium">
                            {sender.email_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </TableCell>
                          <TableCell>
                            {editingEmailSender === sender.email_type ? (
                              <Input
                                value={emailSenderForm.email}
                                onChange={(e) => setEmailSenderForm({...emailSenderForm, email: e.target.value})}
                                className="w-48"
                              />
                            ) : (
                              <span className="text-sm">{sender.email}</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {editingEmailSender === sender.email_type ? (
                              <Input
                                value={emailSenderForm.name}
                                onChange={(e) => setEmailSenderForm({...emailSenderForm, name: e.target.value})}
                                className="w-40"
                              />
                            ) : (
                              <span className="text-sm">{sender.name}</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {editingEmailSender === sender.email_type ? (
                              <Input
                                value={emailSenderForm.description}
                                onChange={(e) => setEmailSenderForm({...emailSenderForm, description: e.target.value})}
                                className="w-48"
                              />
                            ) : (
                              <span className="text-sm text-slate-500">{sender.description}</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {sender.is_custom ? (
                              <Badge variant="outline" className="bg-blue-50 text-blue-700">Custom</Badge>
                            ) : (
                              <Badge variant="outline" className="bg-slate-50">Default</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              {editingEmailSender === sender.email_type ? (
                                <>
                                  <Button
                                    size="sm"
                                    onClick={() => handleSaveEmailSender(sender.email_type)}
                                    disabled={savingEmailSender}
                                  >
                                    {savingEmailSender ? 'Saving...' : 'Save'}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setEditingEmailSender(null)}
                                  >
                                    Cancel
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleEditEmailSender(sender)}
                                  >
                                    <Pencil className="w-3 h-3" />
                                  </Button>
                                  {sender.is_custom && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleResetEmailSender(sender.email_type)}
                                      title="Reset to default"
                                    >
                                      <RotateCcw className="w-3 h-3" />
                                    </Button>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleTestEmailSender(sender.email_type)}
                                    disabled={testingEmail === sender.email_type || !testEmailAddress}
                                    title="Send test email"
                                  >
                                    {testingEmail === sender.email_type ? (
                                      <RefreshCw className="w-3 h-3 animate-spin" />
                                    ) : (
                                      <Send className="w-3 h-3" />
                                    )}
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Email Type Reference</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <h4 className="font-medium mb-2">Candidate & Client Emails</h4>
                      <ul className="space-y-1 text-slate-600">
                        <li>• <strong>candidate_invite</strong> - Invitation emails to candidates</li>
                        <li>• <strong>client_communication</strong> - General client communications</li>
                        <li>• <strong>client_mis</strong> - MIS reports to clients</li>
                        <li>• <strong>insufficiency</strong> - Insufficiency notifications</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-medium mb-2">Verification Team Emails</h4>
                      <ul className="space-y-1 text-slate-600">
                        <li>• <strong>education_verification</strong> - Education verification requests</li>
                        <li>• <strong>employment_verification</strong> - Employment verification requests</li>
                        <li>• <strong>vendor_assignment</strong> - Vendor assignment notifications</li>
                        <li>• <strong>agent_assignment</strong> - Agent assignment notifications</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Email Templates Tab */}
          <TabsContent value="email-templates">
            <div className="grid grid-cols-3 gap-6">
              {/* Templates List */}
              <Card className="col-span-1">
                <CardHeader>
                  <CardTitle className="text-base">Mail Templates</CardTitle>
                  <p className="text-xs text-slate-500">Select a template to edit</p>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y max-h-[500px] overflow-y-auto">
                    {emailTemplates.map((template) => (
                      <div
                        key={template.template_id}
                        className={`p-3 cursor-pointer hover:bg-slate-50 ${selectedTemplate === template.template_id ? 'bg-blue-50 border-l-2 border-blue-500' : ''}`}
                        onClick={() => handleSelectTemplate(template)}
                        data-testid={`template-item-${template.template_id}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm">{template.name}</span>
                          {template.is_custom && (
                            <Badge variant="outline" className="text-xs bg-blue-50">Custom</Badge>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 mt-1 truncate">{template.description}</p>
                        
                        {/* Recipients Info - To/CC */}
                        <div className="flex flex-wrap gap-1 mt-2 text-xs">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full border border-blue-200">
                            <span className="font-medium">To:</span> {template.to_label || template.to || '-'}
                          </span>
                          {template.cc_label && template.cc_label !== '-' && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 rounded-full border border-amber-200">
                              <span className="font-medium">CC:</span> {template.cc_label}
                            </span>
                          )}
                        </div>
                        
                        <div className="flex gap-2 mt-2">
                          <Badge variant="outline" className="text-xs">{template.frequency?.replace(/_/g, ' ')}</Badge>
                          {template.sms_enabled && <Badge variant="outline" className="text-xs bg-green-50">SMS</Badge>}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Template Editor */}
              <Card className="col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>{selectedTemplate ? templateForm.name : 'Select a Template'}</span>
                    {selectedTemplate && (
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => handlePreviewTemplate(selectedTemplate)}>
                          Preview
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleResetTemplate(selectedTemplate)}>
                          <RotateCcw className="w-4 h-4 mr-1" />
                          Reset
                        </Button>
                        <Button size="sm" onClick={handleSaveTemplate} disabled={savingTemplate}>
                          {savingTemplate ? 'Saving...' : 'Save'}
                        </Button>
                      </div>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {selectedTemplate ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Subject Line</Label>
                          <Input
                            value={templateForm.subject || ''}
                            onChange={(e) => setTemplateForm({...templateForm, subject: e.target.value})}
                            placeholder="Email subject with {{variables}}"
                          />
                        </div>
                        <div>
                          <Label>Sender Type</Label>
                          <Select value={templateForm.from_type || ''} onValueChange={(v) => setTemplateForm({...templateForm, from_type: v})}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select sender" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="candidate_invite">Initiation Team</SelectItem>
                              <SelectItem value="client_communication">QC Team</SelectItem>
                              <SelectItem value="client_mis">QC Manager</SelectItem>
                              <SelectItem value="operations">Operations</SelectItem>
                              <SelectItem value="default">Default</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div>
                        <Label>Email Body (HTML)</Label>
                        <textarea
                          className="w-full h-64 p-3 border rounded-md font-mono text-sm"
                          value={templateForm.body_html || ''}
                          onChange={(e) => setTemplateForm({...templateForm, body_html: e.target.value})}
                          placeholder="HTML email content with {{variables}}"
                        />
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="sms_enabled"
                            checked={templateForm.sms_enabled || false}
                            onChange={(e) => setTemplateForm({...templateForm, sms_enabled: e.target.checked})}
                          />
                          <Label htmlFor="sms_enabled">Enable SMS</Label>
                        </div>
                      </div>

                      {templateForm.sms_enabled && (
                        <div>
                          <Label>SMS Template</Label>
                          <Input
                            value={templateForm.sms_template || ''}
                            onChange={(e) => setTemplateForm({...templateForm, sms_template: e.target.value})}
                            placeholder="SMS text with {{variables}}"
                          />
                        </div>
                      )}

                      {/* Test Email */}
                      <div className="pt-4 border-t">
                        <Label>Send Test Email</Label>
                        <div className="flex gap-2 mt-1">
                          <Input
                            type="email"
                            placeholder="Test email address"
                            value={testEmailAddress}
                            onChange={(e) => setTestEmailAddress(e.target.value)}
                            className="max-w-xs"
                          />
                          <Button variant="outline" onClick={handleSendTestTemplate}>
                            <Send className="w-4 h-4 mr-1" />
                            Send Test
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-slate-400">
                      <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Select a template from the list to edit</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Preview Modal */}
            {templatePreview && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setTemplatePreview(null)}>
                <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-auto m-4" onClick={e => e.stopPropagation()}>
                  <div className="p-4 border-b flex justify-between items-center">
                    <h3 className="font-semibold">Email Preview: {templatePreview.template_name}</h3>
                    <Button variant="ghost" size="sm" onClick={() => setTemplatePreview(null)}>✕</Button>
                  </div>
                  <div className="p-4">
                    <p className="text-sm text-slate-500 mb-2"><strong>Subject:</strong> {templatePreview.subject}</p>
                    <div className="border rounded p-4" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(templatePreview.body_html) }} />
                  </div>
                </div>
              </div>
            )}

            {/* Global Settings */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Global Template Settings</span>
                  {editingTemplateSettings ? (
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => setEditingTemplateSettings(false)}>Cancel</Button>
                      <Button size="sm" onClick={handleSaveTemplateSettings}>Save</Button>
                    </div>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => setEditingTemplateSettings(true)}>
                      <Pencil className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Helpline Phone</Label>
                    {editingTemplateSettings ? (
                      <Input
                        value={templateSettings.helpline_phone || ''}
                        onChange={(e) => setTemplateSettings({...templateSettings, helpline_phone: e.target.value})}
                      />
                    ) : (
                      <p className="text-sm mt-1">{templateSettings.helpline_phone || '-'}</p>
                    )}
                  </div>
                  <div>
                    <Label>WhatsApp Number</Label>
                    {editingTemplateSettings ? (
                      <Input
                        value={templateSettings.whatsapp_number || ''}
                        onChange={(e) => setTemplateSettings({...templateSettings, whatsapp_number: e.target.value})}
                      />
                    ) : (
                      <p className="text-sm mt-1">{templateSettings.whatsapp_number || '-'}</p>
                    )}
                  </div>
                  <div>
                    <Label>Support Email</Label>
                    {editingTemplateSettings ? (
                      <Input
                        value={templateSettings.helpline_email || ''}
                        onChange={(e) => setTemplateSettings({...templateSettings, helpline_email: e.target.value})}
                      />
                    ) : (
                      <p className="text-sm mt-1">{templateSettings.helpline_email || '-'}</p>
                    )}
                  </div>
                  <div>
                    <Label>Default Deadline (Days)</Label>
                    {editingTemplateSettings ? (
                      <Input
                        type="number"
                        value={templateSettings.default_deadline_days || 3}
                        onChange={(e) => setTemplateSettings({...templateSettings, default_deadline_days: parseInt(e.target.value)})}
                      />
                    ) : (
                      <p className="text-sm mt-1">{templateSettings.default_deadline_days || 3} days</p>
                    )}
                  </div>
                  <div>
                    <Label>Company Name</Label>
                    {editingTemplateSettings ? (
                      <Input
                        value={templateSettings.company_name || ''}
                        onChange={(e) => setTemplateSettings({...templateSettings, company_name: e.target.value})}
                      />
                    ) : (
                      <p className="text-sm mt-1">{templateSettings.company_name || '-'}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Marketing Materials Tab */}
          <TabsContent value="marketing-materials">
            <div className="space-y-6">
              {/* Upload Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Plus className="w-5 h-5 text-indigo-600" />
                    Upload Marketing Material
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Material Name</Label>
                      <Input
                        value={materialForm.name}
                        onChange={(e) => setMaterialForm({...materialForm, name: e.target.value})}
                        placeholder="e.g., Company Brochure 2026"
                      />
                    </div>
                    <div>
                      <Label>Type</Label>
                      <Select
                        value={materialForm.type}
                        onValueChange={(v) => setMaterialForm({...materialForm, type: v})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="brochure">Brochure</SelectItem>
                          <SelectItem value="pricing_sheet">Pricing Sheet</SelectItem>
                          <SelectItem value="case_study">Case Study</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>File</Label>
                      <div className="flex gap-2">
                        <Input
                          type="file"
                          accept=".pdf,.doc,.docx,.ppt,.pptx"
                          onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                          className="flex-1"
                        />
                        <Button
                          onClick={handleUploadMaterial}
                          disabled={uploadingMaterial || !selectedFile || !materialForm.name}
                          className="bg-indigo-600 hover:bg-indigo-700"
                        >
                          {uploadingMaterial ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            'Upload'
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Materials List */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-emerald-600" />
                    Uploaded Materials ({marketingMaterials.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {marketingMaterials.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                      <p>No marketing materials uploaded yet.</p>
                      <p className="text-sm">Upload brochures, pricing sheets, and case studies to attach to bulk emails.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-4">
                      {marketingMaterials.map((material) => (
                        <div
                          key={material.id}
                          className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${
                              material.type === 'brochure' ? 'bg-indigo-100' :
                              material.type === 'pricing_sheet' ? 'bg-emerald-100' :
                              material.type === 'case_study' ? 'bg-amber-100' : 'bg-slate-100'
                            }`}>
                              <FileText className={`w-5 h-5 ${
                                material.type === 'brochure' ? 'text-indigo-600' :
                                material.type === 'pricing_sheet' ? 'text-emerald-600' :
                                material.type === 'case_study' ? 'text-amber-600' : 'text-slate-600'
                              }`} />
                            </div>
                            <div>
                              <p className="font-medium">{material.name}</p>
                              <div className="flex items-center gap-2 text-sm text-slate-500">
                                <Badge variant="outline" className="text-xs">{material.type.replace('_', ' ')}</Badge>
                                <span>{material.filename}</span>
                                <span>•</span>
                                <span>{(material.file_size / 1024).toFixed(1)} KB</span>
                              </div>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteMaterial(material.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Usage Instructions */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <HelpCircle className="w-5 h-5 text-slate-600" />
                    How to Use
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="prose prose-sm text-slate-600">
                    <ol className="list-decimal pl-4 space-y-2">
                      <li><strong>Upload Materials:</strong> Upload your company brochure and pricing sheet (PDF recommended)</li>
                      <li><strong>Go to Company/Institution Database:</strong> Navigate to Resources → Company Database or Universities</li>
                      <li><strong>Select Recipients:</strong> Use checkboxes to select companies/institutions you want to email</li>
                      <li><strong>Click Bulk Email:</strong> Choose to attach brochure, pricing sheet, or a proposal template</li>
                      <li><strong>Send:</strong> Emails will be sent and recipients will be marked as "Contacted"</li>
                    </ol>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
